// ── Helpers PURS pour la persistance du factLog (chantier D2) ──
// CHEMIN ARGENT (factLog = journal des factures, miroir KV de la table invoices).
//
// Contexte : `saveLog` (App.jsx) construisait la liste à persister depuis un
// `factLog` capturé en closure STALE → last-write-wins → une facture émise
// entre-temps disparaissait du KV (ex. P-2026-0026 perdue). Fix : updater
// functional `prev => next` (StrictMode-safe) + read-merge-write sur le KV FRAIS
// avec dédup par invoiceId pour les émissions.
//
// La shape KV `fbz8-factlog` reste un ARRAY PLAT — inchangée.

// Résout l'argument de saveLog : updater functional OU array (rétro-compat).
// `prev` = l'état React courant fourni par setState. PUR : aucun effet de bord.
function resolveSaveLogArg(arg, prev) {
  return typeof arg === 'function' ? arg(prev) : arg
}

// Construit l'updater d'AJOUT d'une émission, mergé sur le KV FRAIS lu juste
// avant (anti closure stale), avec dédup par invoiceId. `fresh` = parse du KV
// (array) ou null si la lecture a échoué.
//
// ⚠️ SENS INVERSE d'IcalImportBtn : là-bas `fresh===null` ⇒ abort = sécurité
// anti-corruption. ICI un abort = re-perte de la facture (déjà en base = source
// fiscale fiable) → sur `fresh===null` on merge sur `prev` (l'état React), on
// n'abort JAMAIS. La dédup ne porte PAS l'anti-double-clic (garde UI
// `sendingEmail` séparé) : elle évite seulement un doublon sur read-merge.
function makeAppendUpdater(entry, fresh) {
  return (prev) => {
    const base = fresh || prev
    return base.some(f => f.invoiceId === entry.invoiceId) ? base : [entry, ...base]
  }
}

// Message montré quand le journal n'a pas pu être enregistré. Les factures
// elles-mêmes vivent dans la table `invoices` (Historique) : ce journal n'en
// est qu'une copie annexe, et le message ne doit pas faire croire l'inverse.
const MESSAGE_ECHEC_JOURNAL =
  "Le journal annexe des factures n'a pas pu être enregistré. " +
  "Les factures elles-mêmes ne sont pas touchées : elles restent visibles dans l'Historique."

// Fabrique `saveLog`.
//
// ⚠️ DÉFAUT CORRIGÉ (journal figé du 02/06 au 01/10/2026, 30 factures P
// jamais journalisées) : l'ancienne version capturait la valeur DANS l'updater
// de `setFactLog` puis écrivait aussitôt. React 18 n'exécute pas forcément cet
// updater tout de suite : la valeur capturée valait `undefined`, l'écriture
// partait vide, la base la refusait, et le refus mourait en console.
//
// Désormais la nouvelle valeur est calculée ICI, hors de React, sur l'état le
// plus frais (`getCurrent`, une ref tenue à jour), puis posée dans React ET
// écrite. Un échec (valeur qui n'est pas une liste, écriture rendue nulle ou
// en erreur) appelle `onEchec(message)` — à afficher — et rend false.
//
// deps :
//   getCurrent() → array      état le plus frais
//   setCurrent(next)          pose l'état (ref + React)
//   write(texte) → Promise    écrit le texte ; null/undefined = échec
//   onEchec(message)          affiche l'échec à l'écran
function createSaveLog({ getCurrent, setCurrent, write, onEchec }) {
  return async function saveLog(arg) {
    let next
    try {
      next = resolveSaveLogArg(arg, getCurrent())
    } catch (e) {
      console.error('[saveLog] calcul impossible', e)
      onEchec(MESSAGE_ECHEC_JOURNAL)
      return false
    }
    if (!Array.isArray(next)) {
      console.error('[saveLog] valeur refusée (pas une liste)', typeof next)
      onEchec(MESSAGE_ECHEC_JOURNAL)
      return false
    }
    setCurrent(next)
    let res = null
    try {
      res = await write(JSON.stringify(next))
    } catch (e) {
      console.error('[saveLog] écriture en erreur', e)
      res = null
    }
    if (res == null) {
      onEchec(MESSAGE_ECHEC_JOURNAL)
      return false
    }
    return true
  }
}

export { resolveSaveLogArg, makeAppendUpdater, createSaveLog, MESSAGE_ECHEC_JOURNAL }
